/**
 * <product-360-viewer> — drag-to-rotate 360 product spin.
 * Frames are hosted in Shopify Files (Content > Files) and passed in via
 * an inline <script type="application/json" data-frames-json> element.
 * Rendered by snippets/product-360-viewer.liquid.
 */
if (!customElements.get('product-360-viewer')) {
  class Product360Viewer extends HTMLElement {
    connectedCallback() {
      this.frameEl = this.querySelector('[data-frame]');
      this.loaderEl = this.querySelector('[data-loader]');
      this.progressEl = this.querySelector('[data-progress]');
      this.hintEl = this.querySelector('[data-hint]');
      this.playBtn = this.querySelector('[data-play]');

      const framesScript = this.querySelector('[data-frames-json]');
      if (!framesScript || !this.frameEl) return;

      try {
        this.frames = JSON.parse(framesScript.textContent);
      } catch (e) {
        return;
      }
      this.frameCount = this.frames.length;
      if (!this.frameCount) return;

      // -1 flips rotation if a set was shot in the opposite direction
      this.direction = parseInt(this.dataset.direction || '1', 10) || 1;
      this.index = 0;
      this.loaded = false;
      this.loadedCount = 0;
      this.images = [];
      this.velocity = 0;
      this.rafId = null;
      this.spinTimer = null;

      this._bindPointer();
      this._bindKeyboard();
      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => this._toggleSpin());
      }

      // Preload only when the slide is actually near/on screen
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._preload();
            } else {
              this._stopSpin();
            }
          });
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this._stopSpin();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    _preload() {
      if (this.preloading) return;
      this.preloading = true;
      if (this.loaderEl) this.loaderEl.hidden = false;

      this.frames.forEach((src, i) => {
        const img = new Image();
        img.onload = img.onerror = () => {
          this.loadedCount += 1;
          if (this.progressEl) {
            this.progressEl.textContent = Math.round((this.loadedCount / this.frameCount) * 100) + '%';
          }
          if (this.loadedCount >= this.frameCount) this._ready();
        };
        img.src = src;
        this.images[i] = img;
      });
    }

    _ready() {
      this.loaded = true;
      if (this.loaderEl) this.loaderEl.hidden = true;
      this.classList.add('is-ready');
    }

    _show(index) {
      const count = this.frameCount;
      this.index = ((Math.round(index) % count) + count) % count;
      const img = this.images[this.index];
      if (img && img.complete && img.naturalWidth) {
        this.frameEl.src = img.src;
      }
    }

    _dismissHint() {
      if (this.hintEl && !this.hintEl.classList.contains('is-hidden')) {
        this.hintEl.classList.add('is-hidden');
      }
    }

    _bindPointer() {
      this.addEventListener('pointerdown', (e) => {
        if (!this.loaded || e.button > 0) return;
        // Autospin / fullscreen buttons handle their own clicks
        if (e.target.closest('button, media-zoom-button')) return;
        e.preventDefault();
        this._stopSpin();
        if (this.rafId) cancelAnimationFrame(this.rafId);

        this.dragging = true;
        this.classList.add('is-dragging');
        this._dismissHint();

        const rect = this.getBoundingClientRect();
        // One full rotation for a drag across ~2/3 of the viewer width
        this.pxPerFrame = Math.max(3, (rect.width * 0.66) / this.frameCount);
        this.startX = e.clientX;
        this.startIndex = this.index;
        this.lastX = e.clientX;
        this.lastTime = e.timeStamp;
        this.velocity = 0;
        this.setPointerCapture(e.pointerId);
      });

      this.addEventListener('pointermove', (e) => {
        if (!this.dragging) return;
        const dx = e.clientX - this.startX;
        this._show(this.startIndex - (this.direction * dx) / this.pxPerFrame);

        const dt = e.timeStamp - this.lastTime;
        if (dt > 0) {
          // frames per ms, smoothed
          const v = (-this.direction * (e.clientX - this.lastX)) / this.pxPerFrame / dt;
          this.velocity = 0.8 * v + 0.2 * this.velocity;
        }
        this.lastX = e.clientX;
        this.lastTime = e.timeStamp;
      });

      const end = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
        if (this.hasPointerCapture && this.hasPointerCapture(e.pointerId)) {
          this.releasePointerCapture(e.pointerId);
        }
        this._inertia();
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
    }

    _inertia() {
      let v = this.velocity * 16; // frames per ~1 tick
      if (Math.abs(v) < 0.15) return;
      let position = this.index;
      const step = () => {
        v *= 0.93;
        position += v;
        this._show(position);
        if (Math.abs(v) > 0.05) {
          this.rafId = requestAnimationFrame(step);
        }
      };
      this.rafId = requestAnimationFrame(step);
    }

    _bindKeyboard() {
      this.addEventListener('keydown', (e) => {
        if (!this.loaded) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._dismissHint();
          this._show(this.index - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._dismissHint();
          this._show(this.index + 1);
        }
      });
    }

    _toggleSpin() {
      if (!this.loaded) return;
      if (this.spinTimer) {
        this._stopSpin();
        return;
      }
      this._dismissHint();
      this.classList.add('is-spinning');
      if (this.playBtn) this.playBtn.setAttribute('aria-pressed', 'true');
      this.spinTimer = setInterval(() => {
        this._show(this.index + this.direction);
      }, 75);
    }

    _stopSpin() {
      if (!this.spinTimer) return;
      clearInterval(this.spinTimer);
      this.spinTimer = null;
      this.classList.remove('is-spinning');
      if (this.playBtn) this.playBtn.setAttribute('aria-pressed', 'false');
    }
  }

  customElements.define('product-360-viewer', Product360Viewer);
}
